import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { fieldsConfiguration } from '../constants/appConstants';

const GROUP_KEYS = fieldsConfiguration.FIELD_GROUPS.map((group) => group.key);

// Flattens the four buckets to one name → enabled map. A coding code appears in both
// `codes` and `lineItems`, but the BE applies a single flag to both lists, so one
// entry per name is right: the two can never hold different values.
export const indexFieldConfig = (data) => {
  const source = data && typeof data === 'object' ? data : {};

  return GROUP_KEYS.reduce((acc, key) => {
    (Array.isArray(source[key]) ? source[key] : []).forEach((field) => {
      if (field?.name) acc[field.name] = field.enabled !== false;
    });
    return acc;
  }, {});
};

export const isFieldConfigLoaded = (fieldConfig) =>
  !!fieldConfig && Object.keys(fieldConfig).length > 0;

// The question the retired permission flags used to answer, asked of the field.
//
// Three states, not two. A field the company switched off is off. A field the ERP
// does not carry at all is off too — Foundation has no workOrder, so nothing should
// render or sync for it. But before the config has loaded nothing is known, and
// answering "off" there would blank the coding columns and under-sync the ERP lists
// on the first paint after login. Unloaded therefore answers "on": showing a field
// early is visible and self-correcting, hiding one loses data entry.
export const isFieldEnabled = (fieldConfig, name) => {
  if (!isFieldConfigLoaded(fieldConfig)) return true;
  return fieldConfig[name] === true;
};

// A couple of consumers still think in the coarse terms the old flags were phrased in
// — the manual-sync dropdown groups its options as "does this company code jobs and
// costs at all?" rather than per field — so they ask it here instead of each
// rebuilding the same or-chain.
export const codesJobsAndCosts = (isFieldOn) =>
  isFieldOn('userJobId') || isFieldOn('userCostId') || isFieldOn('userCostType');

export const codesServiceOrEquipment = (isFieldOn) =>
  isFieldOn('equipmentNo') || isFieldOn('serviceCode');

// Which document field governs a sync list. A key absent from this map is ungated —
// vendors, accounts, UOM, tax codes and the rest are not fields on a document — and is
// always offered.
export const SYNC_KEY_FIELD = {
  jobIds: 'userJobId',
  costIds: 'userCostId',
  costType: 'userCostType',
  phase: 'userPhases',
  workOrder: 'workOrder',
  scope: 'scope',
  equipment: 'equipmentNo',
  serviceCode: 'serviceCode',
  glDivision: 'glDivision'
};

export const isSyncKeyOn = (isFieldOn, key) => {
  const field = SYNC_KEY_FIELD[key];
  return !field || isFieldOn(field);
};

// Narrows a sync-dropdown option list to the fields the company actually has. The
// coarse questions above pick the base list, which is not enough on their own: a
// company that codes cost codes but not job ids still gets the whole job/cost option
// group, so each option is filtered by its own key here. An option whose every key is
// off disappears, and the aggregate "All" entry keeps only the keys that survive — it
// is what gets sent when the user picks it.
export const filterSyncOptions = (options = [], isFieldOn) =>
  (Array.isArray(options) ? options : [])
    .map((option) => ({
      ...option,
      value: (option?.value || []).filter((key) => isSyncKeyOn(isFieldOn, key))
    }))
    .filter((option) => option.value.length > 0);

export const useFieldConfig = () => {
  const fieldConfig = useSelector((state) => state?.fieldConfig?.fieldConfig);

  return useMemo(
    () => ({
      fieldConfig,
      loaded: isFieldConfigLoaded(fieldConfig),
      isFieldOn: (name) => isFieldEnabled(fieldConfig, name)
    }),
    [fieldConfig]
  );
};
